import { getActivePinia } from 'pinia'

// Stores that must NOT be reset on logout. 'auth' is skipped on purpose:
// login() calls logout() mid-flow and then writes the new tokens/permissions
// into the auth store right after, so wiping it here would clobber them.
const KEEP_STORE_IDS = new Set<string>(['auth'])

/**
 * Reset every in-memory Pinia store to its initial state.
 *
 * Pinia stores hold per-user data (transactions, balances, …) in memory and
 * survive a SPA login by a different user (login navigates via router.push,
 * not a hard page reload). Without this reset, a freshly logged-in user can
 * see the previous user's data until each screen re-fetches. Called from
 * `helpers/utils.ts:logout()`, which every logout path and `login()` funnel
 * through.
 */
export function resetAllPiniaStores(): void {
  try {
    const pinia = getActivePinia()
    if (!pinia) return
    // `_s` is the internal Map of instantiated stores. Pinia has no public
    // "reset all" API, so we iterate it directly.
    ;(pinia as unknown as { _s: Map<string, { $id: string; $reset: () => void }> })._s.forEach(
      (store) => {
        if (KEEP_STORE_IDS.has(store.$id)) return
        try {
          store.$reset()
        } catch {
          // Setup-style stores have no $reset() — nothing to do.
        }
      },
    )
  } catch {
    // Pinia not initialised yet (e.g. a very early logout) — nothing to reset.
  }
}
